import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity, useWindowDimensions} from 'react-native';
import {
  Text, Appbar, ActivityIndicator, Button,
  Switch, TextInput, Snackbar, Divider,
} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const GRID_COLUMNS = 3;
const BOX_GAP = spacing.sm;

const OptionSelectionScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {catalogItemId} = route.params;
  const existingSelections = route.params.optionSelections || route.params.stitchSelections || [];

  const {catalogItemOptionTypes, isLoading, fetchOptionTypesForCatalogItem, clearCatalogItemOptionTypes} = useMeasurementStore();

  const [selections, setSelections] = useState({});
  const [snackbar, setSnackbar] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        await fetchOptionTypesForCatalogItem(catalogItemId);
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || 'Failed to load option types');
      }
    };
    load();
    return () => clearCatalogItemOptionTypes();
  }, [catalogItemId, fetchOptionTypesForCatalogItem, clearCatalogItemOptionTypes]);

  useEffect(() => {
    if (catalogItemOptionTypes.length === 0 || initialized) return;

    const initial = {};
    const assignedTypes = catalogItemOptionTypes.filter(f => f.selected);

    for (const ot of assignedTypes) {
      const existing = existingSelections.find(
        s => s.optionTypeId === ot.optionTypeId || s.stitchFeatureId === ot.optionTypeId
      );
      if (existing) {
        if (ot.renderType === 'TEXT') {
          initial[ot.optionTypeId] = {textValue: existing.textValue || ''};
        } else if (ot.renderType === 'TOGGLE') {
          initial[ot.optionTypeId] = {
            toggled: existing.selectedOptions && existing.selectedOptions.length > 0,
          };
        } else if (ot.renderType === 'SINGLE_SELECT') {
          initial[ot.optionTypeId] = {
            selectedOptionId: existing.selectedOptions?.[0]?.id || null,
          };
        } else if (ot.renderType === 'MULTI_SELECT') {
          initial[ot.optionTypeId] = {
            selectedOptionIds: (existing.selectedOptions || []).map(o => o.id),
          };
        }
      } else {
        if (ot.renderType === 'TEXT') {
          initial[ot.optionTypeId] = {textValue: ''};
        } else if (ot.renderType === 'TOGGLE') {
          initial[ot.optionTypeId] = {toggled: false};
        } else if (ot.renderType === 'SINGLE_SELECT') {
          initial[ot.optionTypeId] = {selectedOptionId: null};
        } else if (ot.renderType === 'MULTI_SELECT') {
          initial[ot.optionTypeId] = {selectedOptionIds: []};
        }
      }
    }

    setSelections(initial);
    setInitialized(true);
  }, [catalogItemOptionTypes, existingSelections, initialized]);

  const handleSave = useCallback(() => {
    const assignedTypes = catalogItemOptionTypes.filter(f => f.selected);
    const result = [];

    for (const ot of assignedTypes) {
      const sel = selections[ot.optionTypeId];
      if (!sel) continue;

      const entry = {
        optionTypeId: ot.optionTypeId,
        optionTypeName: ot.optionTypeName,
        renderType: ot.renderType,
      };

      if (ot.renderType === 'TEXT') {
        if (sel.textValue && sel.textValue.trim()) {
          entry.textValue = sel.textValue.trim();
          result.push(entry);
        }
      } else if (ot.renderType === 'TOGGLE') {
        if (sel.toggled) {
          entry.selectedOptions = ot.options?.length > 0
            ? [{id: ot.options[0].id, name: ot.options[0].name, imageUrl1: ot.options[0].imageUrl1}]
            : [{id: null, name: 'Yes'}];
          result.push(entry);
        }
      } else if (ot.renderType === 'SINGLE_SELECT') {
        if (sel.selectedOptionId) {
          const opt = ot.options.find(o => o.id === sel.selectedOptionId);
          if (opt) {
            entry.selectedOptions = [{id: opt.id, name: opt.name, imageUrl1: opt.imageUrl1}];
            result.push(entry);
          }
        }
      } else if (ot.renderType === 'MULTI_SELECT') {
        if (sel.selectedOptionIds && sel.selectedOptionIds.length > 0) {
          entry.selectedOptions = sel.selectedOptionIds
            .map(id => ot.options.find(o => o.id === id))
            .filter(Boolean)
            .map(o => ({id: o.id, name: o.name, imageUrl1: o.imageUrl1}));
          result.push(entry);
        }
      }
    }

    navigation.navigate({
      name: 'OrderLineDetail',
      params: {optionSelections: result},
      merge: true,
      pop: true,
    });
  }, [catalogItemOptionTypes, selections, navigation]);

  const assignedTypes = catalogItemOptionTypes.filter(f => f.selected);

  if (isLoading && catalogItemOptionTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title="Options" titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {assignedTypes.length === 0 ? (
          <Text style={styles.emptyText}>No option types assigned to this catalog item.</Text>
        ) : (
          assignedTypes.map((ot, index) => (
            <View key={ot.optionTypeId}>
              {index > 0 && <Divider style={styles.divider} />}
              <Text style={styles.featureHeader}>{ot.optionTypeName}</Text>
              <RenderOptionTypeInput ot={ot} selections={selections} setSelections={setSelections} />
            </View>
          ))
        )}
        <View style={{height: spacing.xxl}} />
      </ScrollView>

      <View style={styles.bottomBar}>
        <Button mode="contained" onPress={handleSave}
          style={styles.bottomButton} buttonColor={colors.primary}>
          Save & Continue
        </Button>
      </View>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

function OptionBox({opt, selected, onPress, boxSize}) {
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onPress}
      style={[
        styles.optionBox,
        {width: boxSize, height: boxSize + 28},
        selected && styles.optionBoxSelected,
      ]}>
      <View style={[styles.optionImageWrap, {width: boxSize - 8, height: boxSize - 8}]}>
        {opt.imageUrl1 ? (
          <Image source={{uri: opt.imageUrl1}} style={styles.optionImage} resizeMode="cover" />
        ) : (
          <View style={styles.optionLetterWrap}>
            <Text style={styles.optionLetter}>{(opt.name || '?').charAt(0).toUpperCase()}</Text>
          </View>
        )}
      </View>
      <Text style={[styles.optionName, selected && styles.optionNameSelected]} numberOfLines={2}>
        {opt.name}
      </Text>
      {selected && <View style={styles.selectedBadge} />}
    </TouchableOpacity>
  );
}

function RenderOptionTypeInput({ot, selections, setSelections}) {
  const {width: screenWidth} = useWindowDimensions();
  const contentPadding = spacing.md * 2;
  const totalGap = BOX_GAP * (GRID_COLUMNS - 1);
  const boxSize = Math.floor((screenWidth - contentPadding - totalGap) / GRID_COLUMNS);

  const sel = selections[ot.optionTypeId];
  if (!sel) return null;

  if (ot.renderType === 'TOGGLE') {
    return (
      <View style={styles.toggleRow}>
        <Text style={styles.toggleLabel}>
          {ot.options?.length > 0 ? ot.options[0].name : 'Enable'}
        </Text>
        <Switch
          value={sel.toggled}
          onValueChange={val =>
            setSelections(prev => ({...prev, [ot.optionTypeId]: {toggled: val}}))
          }
          color={colors.primary}
        />
      </View>
    );
  }

  if (ot.renderType === 'SINGLE_SELECT') {
    return (
      <View style={styles.optionGrid}>
        {(ot.options || []).map(opt => (
          <OptionBox
            key={opt.id}
            opt={opt}
            boxSize={boxSize}
            selected={sel.selectedOptionId === opt.id}
            onPress={() =>
              setSelections(prev => ({
                ...prev,
                [ot.optionTypeId]: {
                  selectedOptionId: prev[ot.optionTypeId]?.selectedOptionId === opt.id ? null : opt.id,
                },
              }))
            }
          />
        ))}
      </View>
    );
  }

  if (ot.renderType === 'MULTI_SELECT') {
    return (
      <View style={styles.optionGrid}>
        {(ot.options || []).map(opt => {
          const checked = sel.selectedOptionIds.includes(opt.id);
          return (
            <OptionBox
              key={opt.id}
              opt={opt}
              boxSize={boxSize}
              selected={checked}
              onPress={() => {
                setSelections(prev => {
                  const current = prev[ot.optionTypeId]?.selectedOptionIds || [];
                  const updated = checked
                    ? current.filter(id => id !== opt.id)
                    : [...current, opt.id];
                  return {...prev, [ot.optionTypeId]: {selectedOptionIds: updated}};
                });
              }}
            />
          );
        })}
      </View>
    );
  }

  if (ot.renderType === 'TEXT') {
    return (
      <TextInput
        value={sel.textValue}
        onChangeText={val =>
          setSelections(prev => ({...prev, [ot.optionTypeId]: {textValue: val}}))
        }
        mode="outlined"
        placeholder="Enter details..."
        style={styles.textInput}
        multiline
        numberOfLines={2}
        outlineColor={colors.border}
        activeOutlineColor={colors.primary}
      />
    );
  }

  return null;
}

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  content: {flex: 1, padding: spacing.md},
  emptyText: {textAlign: 'center', color: colors.textSecondary, marginTop: spacing.xxl},
  featureHeader: {
    fontSize: 16, fontWeight: '600', color: colors.text,
    marginTop: spacing.sm, marginBottom: spacing.sm,
  },
  divider: {marginVertical: spacing.sm},
  toggleRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center', paddingVertical: spacing.xs,
  },
  toggleLabel: {fontSize: 14, color: colors.text},
  textInput: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  bottomBar: {
    flexDirection: 'row', padding: spacing.md, backgroundColor: colors.surface,
    gap: spacing.sm, elevation: 4,
  },
  bottomButton: {flex: 1, borderRadius: 8},
  // Grid
  optionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: BOX_GAP,
  },
  optionBox: {
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    alignItems: 'center',
    paddingTop: 4,
    overflow: 'hidden',
  },
  optionBoxSelected: {
    borderColor: colors.primary,
    backgroundColor: '#EDE7F6',
  },
  optionImageWrap: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionImage: {
    width: '100%',
    height: '100%',
  },
  optionLetterWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionLetter: {
    fontSize: 36,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  optionName: {
    fontSize: 11,
    color: colors.text,
    textAlign: 'center',
    paddingHorizontal: 2,
    marginTop: 4,
    lineHeight: 13,
  },
  optionNameSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  selectedBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.primary,
  },
});

export default OptionSelectionScreen;
